import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EModal } from '../../@types/Enums';
import useResponsiveSizes from '../../hooks/useResponsiveSizes';
import PlanModel from '../../models/standard/PlanModel';
import { useDraggingContext } from '../../provider/DraggingProvider';
import { useModalContext } from '../../provider/ModalProvider';
import { usePlan } from '../../provider/PlanProvider';
import { useUserDataContext } from '../../provider/UserDataProvider';
import { immersightButton } from '../../utils/colors';
import InputField from '../common/InputField';
import LanguagePicker from '../common/LanguagePicker';
import MenuDrawer from '../common/MenuDrawer';
import UserProfile from '../common/UserProfile';

interface HeaderProps {
    onLoginClick: () => void;
}

const Header = ({ onLoginClick }: HeaderProps) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
    const {currentUserData} = useUserDataContext();
    const {planModel, setPlanModel} = usePlan();
    const intl = useIntl();
    const { isDragging } = useDraggingContext();
    const sizes = useResponsiveSizes();
    const { activeModal } = useModalContext();

    useEffect(() => {
        if(activeModal === EModal.NONE) {
            setIsDrawerOpen(false);
        }
    }, [activeModal]);

    useEffect(() => {
        if(currentUserData.authentificationtoken !== "") {
            setIsUserLoggedIn(true);
        } else {
            setIsUserLoggedIn(false);
        }
    }, [currentUserData, currentUserData.authentificationtoken]);

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
          const nextName = e.target.value;
          setPlanModel((previousPlan) =>
            new PlanModel(
              nextName,
              previousPlan.id,
              previousPlan.kojeId,
              previousPlan.kojeLevel,
              previousPlan.eventId,
              previousPlan.dataObjectId,
              previousPlan.showroomId,
              previousPlan.appVersion,
              previousPlan.building,
              previousPlan.jsonVersion,
              previousPlan.region
            ),
          );
        },
        [setPlanModel]
      );      

    const handleDrawerToggle = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    // 2. Update handler to use the prop instead of navigate
    const handleLoginButtonClick = () => {
        // navigate("/login"); 
        if (onLoginClick) {
            onLoginClick();
        }
    };

    return (
        <Box
            component={"div"}
            sx={{
                flexGrow: 1,
                pointerEvents: isDragging ? "none" : "auto",
                userSelect: isDragging ? "none" : "auto"
            }}
        >
            <AppBar
                z-index="9999"
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: `${sizes.headerHeight}px`
                    }}
                >
                    <IconButton
                        size="large"
                        edge="start"
                        aria-label="menu"
                        sx={{
                            mr: 2
                        }}
                        onClick={handleDrawerToggle}
                    >
                        <MenuIcon />
                    </IconButton>
                    <InputField
                        id="descriptionInput"
                        placeholder={intl.formatMessage({id: "Header.newPlan"})}
                        initialValue={planModel.name}
                        onChange={handleInputChange}
                        sx={{
                            mr: 2,
                            userSelect: isDragging ? "none" : "auto",
                            pointerEvents: isDragging ? "none" : "auto"
                        }}
                    />
                    <LanguagePicker/>
                    { isUserLoggedIn ?
                        <UserProfile/> :
                        <Button
                            sx={{
                                minWidth: '100px',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                backgroundColor: immersightButton
                            }}
                            variant="contained"
                            onClick={handleLoginButtonClick}
                        >
                            <FormattedMessage id="Header.login"/>
                        </Button>
                    }
                </Toolbar>
            </AppBar>
            <MenuDrawer isOpen={isDrawerOpen} onClose={handleDrawerToggle} />
        </Box>
    );
}

export default Header;
